import { CheckCircle2, Monitor, Moon, Paintbrush, Plus, Sun, type LucideIcon } from "lucide-react";
import { useEffect, useState, type CSSProperties } from "react";
import { glowColors } from "@/shared/utils/color";
import { readColorMode, saveColorMode, type ColorMode } from "@/shell/color-mode";
import {
  CUSTOM_THEME_ID,
  DEFAULT_THEME_COLORS,
  DEFAULT_THEME_ID,
  VAMOO_THEME_ID,
  applyTheme,
  isVamooUnlocked,
  markVamooUnlocked,
  readCustomTheme,
  readCustomVamoo,
  readThemeId,
  saveCustomTheme,
  saveTheme,
  type ThemeColors
} from "@/shell/theme";
import { CustomThemeEditor } from "./CustomThemeEditor";
import { THEME_PRESETS } from "./theme-presets";

/** Claro/escuro mexe no painel de conteúdo; a paleta abaixo continua
 *  mandando na casca. Os dois se combinam — ver `shell/color-mode.ts`. */
const COLOR_MODES: { value: ColorMode; label: string; icon: LucideIcon }[] = [
  { value: "light", label: "Claro", icon: Sun },
  { value: "dark", label: "Escuro", icon: Moon },
  { value: "system", label: "Igual ao sistema", icon: Monitor }
];

/** A bola de cor do cartão de tema (2026-10-16, no lugar da miniatura): o
 *  degradê da paleta — as luzes da cor de destaque e das cores vivas dos
 *  cartões sobre a cor do menu, como a orbe da Home — e, ao lado do nome, a
 *  combinação das cinco cores em pílulas. */
function ThemeSwatch({ colors }: { colors: ThemeColors }) {
  const [glowA, glowB] = glowColors(colors.accent, [colors.card1, colors.card2, colors.card3]);
  return (
    <span
      className="theme-swatch"
      style={
        {
          "--sw-rail": colors.rail,
          "--sw-accent": colors.accent,
          "--sw-a": glowA,
          "--sw-b": glowB
        } as CSSProperties
      }
      aria-hidden="true"
    />
  );
}

function ThemeChips({ colors }: { colors: ThemeColors }) {
  return (
    <span className="theme-chips" aria-hidden="true">
      {[colors.rail, colors.accent, colors.card1, colors.card2, colors.card3].map((color, index) => (
        <i key={index} style={{ background: color }} />
      ))}
    </span>
  );
}

function ThemeCardFoot({
  name,
  description,
  selected,
  colors
}: {
  name: string;
  description: string;
  selected: boolean;
  colors?: ThemeColors;
}) {
  return (
    <div className="theme-app-card-foot">
      <div className="theme-app-title-group">
        <strong>{name}</strong>
        <span>{description}</span>
        {colors && <ThemeChips colors={colors} />}
      </div>
      {selected && (
        <span className="theme-app-selected-badge">
          <CheckCircle2 size={14} /> Ativo
        </span>
      )}
    </div>
  );
}

/** O tema efetivamente em uso: "custom" sem cores salvas cai no padrão. */
function activeThemeId(): string {
  const saved = readThemeId() ?? DEFAULT_THEME_ID;
  return saved === CUSTOM_THEME_ID && !readCustomTheme() ? DEFAULT_THEME_ID : saved;
}

/** As abas que a tela tem.
 *
 *  "Notificações" e "Fluxo de Análise" saíram em 2026-09-08. A primeira
 *  oferecia dois toggles de e-mail e NÃO existe envio de e-mail em lugar
 *  nenhum do sistema — as preferências de notificação serão desenhadas
 *  junto com quem for enviar. A segunda tinha um único controle,
 *  "Reclassificação Automática", ligado a `useState` e mais nada: não
 *  persistia, ninguém lia, e o texto prometia um recálculo de CENA
 *  automático que o produto deliberadamente NÃO faz (histórico não se
 *  mexe sozinho). A aba do modo da Mestra Help saiu em 2026-10-13 (o modo
 *  é sempre "padrao"), e com ela toda leitura de `/me/preferences`.
 *
 *  Decisões da dona; ver `web_api/application/preferences.py`. */
export type PreferenceTab = "appearance";

/**
 * A seção de Aparência de Configurações, no desenho dos painéis de Ajustes
 * (ver `ProfilePage`). Tudo aqui é LOCAL (`localStorage` + atributos no
 * `<html>`): o modo claro/escuro e o tema — pronto ou personalizado com cinco
 * cores (release de 2026-10-15) — nunca passam pela API.
 */
export function PreferencesPage(_props: { tab?: PreferenceTab } = {}) {
  const [currentTheme, setCurrentTheme] = useState<string>(DEFAULT_THEME_ID);
  const [customColors, setCustomColors] = useState<ThemeColors | null>(readCustomTheme);
  // O VAMOO é tema oculto: só entra na grade de quem digitou o código no
  // editor (`isVamooUnlocked`, marca no `localStorage`).
  const [vamooUnlocked, setVamooUnlocked] = useState(isVamooUnlocked);
  const presets = vamooUnlocked ? THEME_PRESETS : THEME_PRESETS.filter(preset => !preset.secret);
  const [customVamoo, setCustomVamoo] = useState(readCustomVamoo);
  const [editingCustom, setEditingCustom] = useState(false);
  const [colorMode, setColorMode] = useState<ColorMode>(readColorMode);
  const [feedback, setFeedback] = useState<{ type: "success" | "error"; message: string } | null>(null);

  useEffect(() => {
    const themeId = activeThemeId();
    setCurrentTheme(themeId);
    applyTheme(themeId);
  }, []);

  const changeTheme = (themeId: string) => {
    // Fechar o editor primeiro: a limpeza dele reaplica o tema SALVO,
    // que a esta altura já é o escolhido.
    setEditingCustom(false);
    setCurrentTheme(themeId);
    saveTheme(themeId);
    setFeedback({ type: "success", message: "Tema da plataforma aplicado com sucesso." });
  };

  /** O editor abre com o tema do usuário, ou — na primeira vez — com as
   *  cores do tema pronto em uso, para ninguém começar do zero. */
  const editorStartColors = (): ThemeColors =>
    customColors ??
    THEME_PRESETS.find(preset => preset.id === currentTheme)?.colors ??
    DEFAULT_THEME_COLORS;

  const openEditor = () => {
    setFeedback(null);
    setEditingCustom(true);
  };

  const saveCustom = (colors: ThemeColors, vamoo: boolean) => {
    saveCustomTheme(colors, vamoo);
    setCustomColors(colors);
    setCustomVamoo(vamoo);
    if (vamoo) setVamooUnlocked(true);
    setCurrentTheme(CUSTOM_THEME_ID);
    setEditingCustom(false);
    setFeedback({ type: "success", message: "Seu tema foi salvo e aplicado." });
  };

  /** O editor abre com o fundo VAMOO ligado quando o tema salvo o tem, ou
   *  quando o cartão VAMOO pronto está em uso e ainda não há tema próprio. */
  const editorStartVamoo = (): boolean => (customColors ? customVamoo : currentTheme === VAMOO_THEME_ID);

  /** O código de desbloqueio no editor: o editor já ligou o fundo e segue
   *  aberto; aqui só entra a marca que faz o cartão pronto aparecer. */
  const unlockVamooTheme = () => {
    markVamooUnlocked();
    setVamooUnlocked(true);
  };

  const changeColorMode = (mode: ColorMode) => {
    setColorMode(mode);
    saveColorMode(mode);
    setFeedback({ type: "success", message: "Modo de cor aplicado com sucesso." });
  };

  return (
    <div className="ajustes-panel account-panel">
      <header className="panel-header">
        <div>
          <h2>Aparência e temas</h2>
          <p>Personalize sua experiência visual na plataforma.</p>
        </div>
      </header>

      {feedback && (
        <div className={`account-feedback ${feedback.type}`}>
          {feedback.type === "success" && <CheckCircle2 size={16} />}
          <span>{feedback.message}</span>
        </div>
      )}

      <section className="panel-section">
        <h3>Modo claro ou escuro</h3>
        <p className="account-section-hint">
          Define as cores da área de conteúdo. "Igual ao sistema" acompanha a configuração do seu computador.
        </p>
        {/* Rádio nativo: foco, Espaço e setas de graça, e leitor de tela
            anuncia a escolha. */}
        <div className="color-mode-options" role="radiogroup" aria-label="Modo de cor">
          {COLOR_MODES.map(({ value, label, icon: Icon }) => (
            <label key={value} className={`color-mode-option ${colorMode === value ? "selected" : ""}`}>
              <input
                type="radio"
                className="mode-option-input"
                name="color-mode"
                value={value}
                checked={colorMode === value}
                onChange={() => changeColorMode(value)}
              />
              <Icon size={16} aria-hidden="true" />
              <span>{label}</span>
            </label>
          ))}
        </div>
      </section>

      {editingCustom && (
        <CustomThemeEditor
          initialColors={editorStartColors()}
          initialVamoo={editorStartVamoo()}
          onSave={saveCustom}
          onCancel={() => setEditingCustom(false)}
          onUnlockCode={unlockVamooTheme}
        />
      )}

      <section className="panel-section">
        <div className="account-section-head">
          <div>
            <h3>Tema e paleta de cores</h3>
            <p className="account-section-hint">Escolha um tema pronto ou monte o seu com as cores que preferir.</p>
          </div>
          {/* "Criar seu tema" já é um cartão da grade; o botão do topo fica só
              pra EDITAR um tema que existe (2026-10-16). */}
          {!editingCustom && customColors && (
            <button type="button" className="secondary-button" onClick={openEditor}>
              <Paintbrush size={15} aria-hidden="true" /> Editar seu tema
            </button>
          )}
        </div>

        <div className="theme-palette-grid">
          {presets.map(theme => {
            const isSelected = !editingCustom && currentTheme === theme.id;
            return (
              <button
                key={theme.id}
                type="button"
                className={`theme-app-card ${isSelected ? "selected" : ""}`}
                onClick={() => changeTheme(theme.id)}
              >
                <ThemeSwatch colors={theme.colors} />
                <ThemeCardFoot name={theme.name} description={theme.description} selected={isSelected} colors={theme.colors} />
              </button>
            );
          })}

          {/* O tema do usuário entra como mais um cartão da grade:
              com cores salvas, aplica com um clique como os outros;
              sem, o cartão é o convite pra criar. */}
          {customColors ? (
            <button
              type="button"
              className={`theme-app-card ${editingCustom || currentTheme === CUSTOM_THEME_ID ? "selected" : ""}`}
              onClick={() => changeTheme(CUSTOM_THEME_ID)}
            >
              <ThemeSwatch colors={customColors} />
              <ThemeCardFoot
                name="Seu tema"
                description="As cores que você escolheu"
                colors={customColors}
                selected={!editingCustom && currentTheme === CUSTOM_THEME_ID}
              />
            </button>
          ) : (
            <button type="button" className="theme-app-card theme-app-card-create" onClick={openEditor}>
              <span className="theme-app-create-icon" aria-hidden="true"><Plus size={18} /></span>
              <div className="theme-app-card-foot">
                <div className="theme-app-title-group">
                  <strong>Criar seu tema</strong>
                  <span>Escolha as cores do menu, do destaque e dos cartões de resumo</span>
                </div>
              </div>
            </button>
          )}
        </div>
        {/* O idioma da interface saiu da tela em 2026-10-15 (decisão da dona:
            só há português). Quando houver outro, volta como escolha. */}
      </section>
    </div>
  );
}
